import React, { useState } from "react";
import { CalcField, Stat, SplitBar, CalcShell } from "@/components/calculators/CalcKit";
import { sipFV, lumpsumFV, sipForTarget, emi, fmtINR } from "@/components/calculators/meta";

export const SipCalc = () => {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);
  const invested = monthly * years * 12;
  const fv = sipFV(monthly, rate, years);
  return (
    <CalcShell
      inputs={<>
        <CalcField id="sip-monthly" label="Monthly investment" value={monthly} onChange={setMonthly} min={500} max={200000} step={500} prefix="₹" money />
        <CalcField id="sip-rate" label="Expected annual return" value={rate} onChange={setRate} min={1} max={25} step={0.5} suffix="%" />
        <CalcField id="sip-years" label="Time period" value={years} onChange={setYears} min={1} max={40} suffix=" yrs" />
      </>}
      results={<>
        <Stat id="sip-fv" label="Estimated value" value={fv} sub={`After ${years} years`} accent />
        <div className="grid grid-cols-2 gap-4">
          <Stat id="sip-invested" label="Total invested" value={invested} />
          <Stat id="sip-gains" label="Estimated gains" value={fv - invested} />
        </div>
        <div className="rounded-2xl border border-navy/[0.08] bg-white p-5"><SplitBar a={invested} b={fv - invested} labelA="Invested" labelB="Gains" /></div>
      </>}
    />
  );
};

export const LumpsumCalc = () => {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(10);
  const [years, setYears] = useState(10);
  const fv = lumpsumFV(amount, rate, years);
  return (
    <CalcShell
      inputs={<>
        <CalcField id="ls-amount" label="One-time investment" value={amount} onChange={setAmount} min={10000} max={50000000} step={10000} prefix="₹" money />
        <CalcField id="ls-rate" label="Expected annual return" value={rate} onChange={setRate} min={1} max={25} step={0.5} suffix="%" />
        <CalcField id="ls-years" label="Time period" value={years} onChange={setYears} min={1} max={40} suffix=" yrs" />
      </>}
      results={<>
        <Stat id="ls-fv" label="Estimated value" value={fv} sub={`After ${years} years`} accent />
        <div className="grid grid-cols-2 gap-4">
          <Stat id="ls-invested" label="Invested" value={amount} />
          <Stat id="ls-gains" label="Estimated gains" value={fv - amount} />
        </div>
        <div className="rounded-2xl border border-navy/[0.08] bg-white p-5"><SplitBar a={amount} b={fv - amount} labelA="Invested" labelB="Gains" /></div>
      </>}
    />
  );
};

export const GoalCalc = () => {
  const [target, setTarget] = useState(5000000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(12);
  const monthly = sipForTarget(target, rate, years);
  const invested = monthly * years * 12;
  return (
    <CalcShell
      inputs={<>
        <CalcField id="goal-target" label="Target amount" value={target} onChange={setTarget} min={100000} max={100000000} step={100000} prefix="₹" money />
        <CalcField id="goal-rate" label="Expected annual return" value={rate} onChange={setRate} min={1} max={25} step={0.5} suffix="%" />
        <CalcField id="goal-years" label="Years to goal" value={years} onChange={setYears} min={1} max={40} suffix=" yrs" />
      </>}
      results={<>
        <Stat id="goal-monthly" label="Monthly SIP needed" value={monthly} sub={`To reach ${fmtINR(target)} in ${years} years`} accent />
        <div className="grid grid-cols-2 gap-4">
          <Stat id="goal-invested" label="You'd invest" value={invested} />
          <Stat id="goal-gains" label="Growth does the rest" value={target - invested} />
        </div>
        <div className="rounded-2xl border border-navy/[0.08] bg-white p-5"><SplitBar a={invested} b={target - invested} labelA="Invested" labelB="Gains" /></div>
      </>}
    />
  );
};

export const EmiCalc = () => {
  const [principal, setPrincipal] = useState(3000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const e = emi(principal, rate, years);
  const total = e * years * 12;
  return (
    <CalcShell
      inputs={<>
        <CalcField id="emi-principal" label="Loan amount" value={principal} onChange={setPrincipal} min={50000} max={100000000} step={50000} prefix="₹" money />
        <CalcField id="emi-rate" label="Interest rate (p.a.)" value={rate} onChange={setRate} min={1} max={30} step={0.1} suffix="%" />
        <CalcField id="emi-years" label="Tenure" value={years} onChange={setYears} min={1} max={30} suffix=" yrs" />
      </>}
      results={<>
        <Stat id="emi-monthly" label="Monthly EMI" value={e} sub={`For ${years} years at ${rate}%`} accent />
        <div className="grid grid-cols-2 gap-4">
          <Stat id="emi-interest" label="Total interest" value={total - principal} />
          <Stat id="emi-total" label="Total payment" value={total} />
        </div>
        <div className="rounded-2xl border border-navy/[0.08] bg-white p-5"><SplitBar a={principal} b={total - principal} labelA="Principal" labelB="Interest" /></div>
      </>}
      note="Illustrative only. Actual EMI depends on the lender's rate, fees and schedule. Loans are arranged through lending partners; Auxieron is not a lender."
    />
  );
};

export const RetirementCalc = () => {
  const [age, setAge] = useState(30);
  const [retireAge, setRetireAge] = useState(60);
  const [expense, setExpense] = useState(60000);
  const [inflation, setInflation] = useState(6);
  const [rate, setRate] = useState(12);
  const years = Math.max(1, retireAge - age);
  const futureMonthly = expense * Math.pow(1 + inflation / 100, years);
  const corpus = futureMonthly * 12 * 25;
  const monthly = sipForTarget(corpus, rate, years);
  return (
    <CalcShell
      inputs={<>
        <div className="grid grid-cols-2 gap-5">
          <CalcField id="ret-age" label="Current age" value={age} onChange={setAge} min={18} max={65} suffix=" yrs" />
          <CalcField id="ret-retire" label="Retirement age" value={retireAge} onChange={setRetireAge} min={40} max={75} suffix=" yrs" />
        </div>
        <CalcField id="ret-expense" label="Monthly expenses today" value={expense} onChange={setExpense} min={10000} max={1000000} step={5000} prefix="₹" money />
        <CalcField id="ret-inflation" label="Expected inflation" value={inflation} onChange={setInflation} min={2} max={12} step={0.5} suffix="%" />
        <CalcField id="ret-rate" label="Expected return till retirement" value={rate} onChange={setRate} min={1} max={20} step={0.5} suffix="%" />
      </>}
      results={<>
        <Stat id="ret-corpus" label="Retirement corpus needed" value={corpus} sub={`≈ 25× annual expenses at age ${retireAge}`} accent />
        <div className="grid grid-cols-2 gap-4">
          <Stat id="ret-future-expense" label="Monthly expense then" value={futureMonthly} sub={`In ${years} years`} />
          <Stat id="ret-monthly" label="Monthly SIP needed" value={monthly} sub="Starting now" />
        </div>
      </>}
      note="Illustrative only. Uses a 25× annual-expense anchor and constant return/inflation assumptions. Real plans also consider existing savings, EPF/NPS and post-retirement returns."
    />
  );
};

export const TermCalc = () => {
  const [income, setIncome] = useState(1200000);
  const [years, setYears] = useState(15);
  const [loans, setLoans] = useState(2000000);
  const [goals, setGoals] = useState(3000000);
  const [existing, setExisting] = useState(0);
  const need = Math.max(0, income * years + loans + goals - existing);
  return (
    <CalcShell
      inputs={<>
        <CalcField id="term-income" label="Annual income" value={income} onChange={setIncome} min={100000} max={50000000} step={50000} prefix="₹" money />
        <CalcField id="term-years" label="Years family needs support" value={years} onChange={setYears} min={1} max={40} suffix=" yrs" />
        <CalcField id="term-loans" label="Outstanding loans" value={loans} onChange={setLoans} min={0} max={100000000} step={100000} prefix="₹" money />
        <CalcField id="term-goals" label="Future goals (education, etc.)" value={goals} onChange={setGoals} min={0} max={100000000} step={100000} prefix="₹" money />
        <CalcField id="term-existing" label="Existing cover & liquid assets" value={existing} onChange={setExisting} min={0} max={100000000} step={100000} prefix="₹" money />
      </>}
      results={<>
        <Stat id="term-need" label="Suggested life cover" value={need} sub="Income replacement + liabilities + goals − existing" accent />
        <div className="grid grid-cols-2 gap-4">
          <Stat id="term-replace" label="Income replacement" value={income * years} />
          <Stat id="term-liab" label="Loans + goals" value={loans + goals} />
        </div>
      </>}
      note="A simplified estimate. The right cover also depends on age, dependants, inflation and existing assets. Insurance is the subject matter of solicitation."
    />
  );
};

export const CALCS = {
  sip: SipCalc,
  lumpsum: LumpsumCalc,
  goal: GoalCalc,
  emi: EmiCalc,
  retirement: RetirementCalc,
  term: TermCalc,
};
